import { useNavigate } from '@tanstack/react-router'
import type { DataSource, PitchDocument, PitchHistoryEntry } from '@workspace/seedforge-ui/pitch'
import { StartPitchView } from '@workspace/seedforge-ui/pitch'
import { useMemo, useState } from 'react'
import { VideoPreviewModal } from '../../../components/video-preview-modal'
import { useStartupContext } from '../../../contexts/startup-guard'
import {
  useCreatePitchSessionMutation,
  useStartupDocumentsQuery,
  useTeamMembersQuery,
} from '../../../hooks/api'
import { useBasePath } from '../../../hooks/use-base-path'

function fileExtension(fileName: string): string {
  return fileName.split('.').pop()?.toUpperCase() || ''
}

export interface StartSessionHistoryEntry {
  createdAt: string
  status: string
  evaluations?: { score?: number | null }[]
}

/**
 * The "Ready to pitch?" page — session cost, what we'll feed the AI investor,
 * and past sessions. Creating a session sends the founder back to the pitch
 * route, which takes over with the full-screen preparing / live views.
 */
export function StartSessionView({
  history: rawHistory,
}: {
  history?: StartSessionHistoryEntry[]
}) {
  const { startup, billing } = useStartupContext()
  const sessionsAvailable = billing?.sessionsAvailable ?? 0
  const canStartSession = billing?.canStartSession ?? false
  const promoSession = billing?.promoSession ?? false
  const basePath = useBasePath()
  const navigate = useNavigate()
  const { data: startupDocs } = useStartupDocumentsQuery(startup.id)
  const { data: teamMembers } = useTeamMembersQuery(startup.id)
  const pitchDeckDoc = startupDocs?.find((sd) => sd.type === 'PITCH_DECK')

  const createPitch = useCreatePitchSessionMutation({
    onSuccess: () => navigate({ to: `${basePath}/pitch` }),
  })

  const handleStartPitch = () => {
    createPitch.mutate({
      body: {
        startupId: startup.id,
        pitchDeckId: pitchDeckDoc?.documentId,
      },
    })
  }

  const hasPitchDeck = !!pitchDeckDoc
  const regularDocs = useMemo(
    () => startupDocs?.filter((sd) => sd.type !== 'PITCH_DECK') ?? [],
    [startupDocs]
  )
  const allTeamHaveLinkedin = !!teamMembers?.length && teamMembers.every((m) => !!m.linkedinUrl)
  const hasWebsite = !!startup.websiteUrl
  const isFundraising = !!startup.isFundraising

  const documents: PitchDocument[] = useMemo(
    () =>
      regularDocs.map((sd) => ({
        name: sd.label || sd.document.fileName,
        type: fileExtension(sd.document.fileName),
        uploaded: true,
      })),
    [regularDocs]
  )

  const dataSources: DataSource[] = useMemo(
    () => [
      { label: 'Fundraising status', available: isFundraising, required: true },
      { label: 'Pitch Deck', available: hasPitchDeck, required: true },
      { label: 'Documents', available: regularDocs.length > 0 },
      { label: 'Team profiles', available: allTeamHaveLinkedin },
      { label: 'Public web', available: hasWebsite },
    ],
    [isFundraising, hasPitchDeck, regularDocs.length, allTeamHaveLinkedin, hasWebsite]
  )

  const history: PitchHistoryEntry[] = (rawHistory ?? []).map((s) => ({
    date: new Date(s.createdAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    overallScore: s.evaluations?.[0]?.score ?? 0,
    readiness:
      s.status === 'COMPLETED' ? 'Completed' : s.status === 'FAILED' ? 'Failed' : 'Incomplete',
  }))

  const [demoOpen, setDemoOpen] = useState(false)

  return (
    <>
      <StartPitchView
        sessionsAvailable={sessionsAvailable}
        canStartSession={canStartSession}
        promoSession={promoSession}
        onPlayDemo={() => setDemoOpen(true)}
        profile={{
          startupName: startup.name,
          initials: startup.name.charAt(0).toUpperCase(),
          logoUrl: startup.logoUrl,
          productStage: startup.productStage,
          sector: startup.category?.name,
          location: startup.location,
          foundingDate: startup.foundingDate,
          isFundraising: startup.isFundraising,
          fundraisingStage: startup.fundraisingStage,
          fundraisingAmount: startup.fundraisingAmount,
        }}
        documents={documents}
        dataSources={dataSources}
        previousPitches={history}
        onUpdateDataSources={() => navigate({ to: `${basePath}/profile` })}
        onStartPitch={handleStartPitch}
        onBuySession={() => navigate({ to: `${basePath}/billing` })}
        isStartingPitch={createPitch.isPending}
      />
      <VideoPreviewModal
        videoUrl="https://seedforge.com/demo.mp4"
        title="What to expect from the AI session"
        open={demoOpen}
        onOpenChange={setDemoOpen}
      />
    </>
  )
}
